<script lang="ts">
	import CardBadge from '$lib/components/ui/CardBadge.svelte';
	import FlexibleImageCollage from '$lib/components/ui/FlexibleImageCollage.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import type { ItineraryPreview } from '$lib/features/itinerary/itinerary.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import { listFormatByAnd } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { type NavLink, UrlUtil } from '$lib/utils/url.util';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconCalendarMonth from '~icons/mdi/calendar-month';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.ITINERARY;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const meta = $derived.by(() => {
		const firstAvailableImage: CoverImage = output.items.flatMap((a) => a.dailyPlans.flatMap((b) => b.images)).at(0)!;
		const itemsLength = output.items.length;
		const durations = output.items.map((a) => a.durationDays).sort((a, b) => a - b);
		const durationsText = listFormatByAnd(durations.map((d) => d.toString()));
		return {
			title: `${location.city.label} Itineraries: ${durationsText}-Day Plans`,
			description: `Plan your trip to ${location.city.label} with ${itemsLength} curated itineraries — ${durationsText} days — with daily stops mapped out, so all you have to do is explore.`,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const combinedJsonLd = $derived(data.output.jsonLdBreadcrumbs);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`Curated Itineraries for ${location.city.label}`}
	description={`Whether you have a weekend or a week, we've planned it out for you. Browse our day-by-day guides to make the most of your trip.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<ul class="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-2">
		{#each output.items as itinerary, i (itinerary.feature.id)}
			<li>
				{@render ItineraryCard(itinerary, itinerary.feature.images, i === 0)}
			</li>
		{/each}
	</ul>

	<KeepExploringSection categories={output.keepExploring} {location} />
</div>

{#snippet ItineraryCard(itinerary: ItineraryPreview, images: CoverImage[], eagerLoadImages: boolean)}
	{const featureNav = $derived<NavLink>(
		UrlUtil.toCityFeature({ location, type: itinerary.feature.type, featureSlug: itinerary.feature.slug })
	)}
	<a
		class="group relative flex h-full flex-col overflow-hidden rounded-box border border-base-200 bg-base-200 shadow-sm transition-all duration-300 hover:-translate-y-1"
		title="View {itinerary.feature.englishName} itinerary"
		href={`/${featureNav.href}`}
		data-sveltekit-preload-data={featureNav.preloadData}
		data-sveltekit-preload-code={featureNav.preloadCode}
	>
		<div class="relative h-[33vh] w-full overflow-hidden">
			<FlexibleImageCollage {images} isEagerLoad={eagerLoadImages} />
			<div class="absolute top-4 left-4">
				<CardBadge text={`${itinerary.durationDays} ${itinerary.durationDays === 1 ? 'Day' : 'Days'}`} Icon={IconCalendarMonth} />
			</div>
		</div>

		<div class="flex flex-1 flex-col gap-5 p-6 md:p-8">
			<h2 class="text-3xl transition-colors group-hover:text-primary">
				{itinerary.feature.englishName}
			</h2>

			<p class="line-clamp-3 text-base text-base-content/70">
				{itinerary.feature.description}
			</p>

			<div class="flex-1 space-y-3">
				{@render PlanTimeline()}
			</div>

			<div class="divider m-0"></div>

			<div class="mt-auto flex items-center justify-between">
				{@render Footer()}
			</div>
		</div>
	</a>

	{#snippet PlanTimeline()}
		<div class="text-xs font-bold tracking-widest text-base-content/70">DAILY THEME</div>
		{#if itinerary.dailyPlans && itinerary.dailyPlans.length > 0}
			<div class="flex flex-col gap-2">
				{#each itinerary.dailyPlans.slice(0, 3) as day (day.dayNumber)}
					<div class="flex items-start gap-3">
						<span class="shrink-0 rounded bg-base-100 px-2 py-0.5 font-mono text-xs font-bold shadow-sm">
							Day {day.dayNumber}
						</span>
						<span class="line-clamp-1 text-sm font-medium text-base-content/80">{day.themeOfDay}</span>
					</div>
				{/each}
				{#if itinerary.dailyPlans.length > 3}
					<i class="text-xs text-base-content/70">
						+ {itinerary.dailyPlans.length - 3} more days...
					</i>
				{/if}
			</div>
		{/if}
	{/snippet}

	{#snippet Footer()}
		{const totalStops = $derived<number>(itinerary.dailyPlans.reduce((acc, plan) => acc + plan.stopsCounter, 0))}
		<div class="text-sm font-medium text-base-content/70">
			{totalStops} Stops
		</div>
		<span
			class="inline-flex items-center gap-1 font-mono text-sm font-bold text-primary transition-transform group-hover:translate-x-1"
		>
			view itinerary <IconArrowRight />
		</span>
	{/snippet}
{/snippet}
